import { appendVimeoEmbed, createYoutubeEmbed } from "../utils/video-embeds.js";

export function eventPortfolio() {

  gsap.registerPlugin(ScrollTrigger);

  const EMBED_CLASS = "event-slider__video-embed";

  const SLIDER_BREAKPOINT = 1024;

  const MOBILE_EMBED_CLASS = "event-slider__video-wrap--has-embed";

  /** Desktop pinned slider: crossfade poster → video (CSS) after media is ready. */
  const REVEAL_CLASS = "is-video-revealed";

  function isSliderActive() {
    return window.innerWidth >= SLIDER_BREAKPOINT;
  }

  /** Narrow layout: stacked grid + ScrollTrigger viewport playback (no pinned slider). */
  function isMobilePortfolio() {
    return !isSliderActive();
  }

  function markMobileEmbedActive(wrap) {
    if (!wrap || !isMobilePortfolio()) return;
    wrap.classList.add(MOBILE_EMBED_CLASS);
  }

  /** Match loader/fullcol-video: default type mp4 so local files work if type is omitted. */
  function readVideoWrapData(wrap) {
    if (!wrap) return { type: "mp4", src: "", id: "", posterSrc: "" };
    const type = (wrap.getAttribute("data-video-type") || "mp4").toLowerCase().trim();
    const src = (wrap.getAttribute("data-video-src") || "").trim();
    const id = (wrap.getAttribute("data-video-id") || "").trim();
    const posterSrc = wrap.getAttribute("data-poster-src") || "";
    return { type, src, id, posterSrc };
  }

  function isWrapLocalVideo(wrap) {
    const { type, src } = readVideoWrapData(wrap);
    return (
      !!src &&
      (type === "mp4" ||
        type === "webm" ||
        type === "ogg" ||
        /\.(mp4|webm|ogg)(\?|#|$)/i.test(src))
    );
  }

  /** Resolve relative paths (e.g. assets/...) for mobile Safari / file origins. */
  function resolveMediaSrc(src) {
    try {
      return new URL(src, document.baseURI).href;
    } catch {
      return src;
    }
  }

  /**
   * Mobile WebKit often drops play() if it runs before layout or only one canplay path fires.
   * Use rAF + load() + several play attempts (muted + inline is required for autoplay).
   */
  function mountAndPlayLocalVideo(wrap, src, posterSrc) {
    const resolved = resolveMediaSrc(src);
    let video = wrap.querySelector("video");
    if (!video) {
      video = document.createElement("video");
      video.className = "event-slider__video";
      wrap.appendChild(video);
    }

    if (video.src !== resolved) {
      video.src = resolved;
      video.setAttribute("src", src);
    }

    video.muted = true;
    video.defaultMuted = true;
    video.setAttribute("muted", "");
    video.loop = true;
    video.playsInline = true;
    video.setAttribute("playsinline", "");
    video.setAttribute("webkit-playsinline", "");
    video.autoplay = true;
    video.preload = "auto";

    if (!isMobilePortfolio() && posterSrc) {
      video.poster = posterSrc;
    } else {
      video.removeAttribute("poster");
    }

    markMobileEmbedActive(wrap);

    const tryPlay = () => {
      if (!video.isConnected) return;
      video.play().catch(() => {});
    };

    const armPlayback = () => {
      video.load();
      video.addEventListener("loadeddata", tryPlay, { once: true });
      video.addEventListener("canplay", tryPlay, { once: true });
      [0, 80, 250, 700, 1500].forEach((ms) => setTimeout(tryPlay, ms));
    };

    requestAnimationFrame(() => {
      requestAnimationFrame(armPlayback);
    });
  }

  function findEmbedIframe(wrap) {
    const host = wrap.querySelector(`.${EMBED_CLASS}`);
    if (host?.tagName === "IFRAME") return host;
    if (host) return host.querySelector("iframe");
    return wrap.querySelector("iframe");
  }

  function sendYoutubeCommand(iframe, action) {
    if (!iframe?.contentWindow) return;
    try {
      const func = action === "pause" ? "pauseVideo" : "playVideo";
      iframe.contentWindow.postMessage(
        JSON.stringify({ event: "command", func, args: [] }),
        "https://www.youtube.com"
      );
    } catch (_) {}
  }

  function sendVimeoCommand(iframe, action) {
    if (!iframe?.contentWindow) return;
    try {
      const method = action === "pause" ? "pause" : "play";
      iframe.contentWindow.postMessage(JSON.stringify({ method }), "https://player.vimeo.com");
    } catch (_) {}
  }

  /** Mobile viewport mode: pause only — keep iframes so poster never flashes back. */
  function pauseMobileEmbed(wrap) {
    const { type } = readVideoWrapData(wrap);
    if (isWrapLocalVideo(wrap)) {
      const video = wrap.querySelector("video");
      if (video) {
        video.pause();
        video.currentTime = 0;
      }
    } else if (type === "youtube") {
      const ifr = findEmbedIframe(wrap);
      if (ifr) sendYoutubeCommand(ifr, "pause");
    } else if (type === "vimeo") {
      const ifr = findEmbedIframe(wrap);
      if (ifr) sendVimeoCommand(ifr, "pause");
    }
  }

  function setupPosters(slides) {
    slides.forEach((slide) => {
      const wrap = slide.querySelector('.event-slider__video-wrap');
      if (!wrap) return;
      const posterAttr = wrap.getAttribute('data-poster-src');
      if (!isMobilePortfolio() && posterAttr) {
        let poster = wrap.querySelector('.event-slider__poster');
        if (!poster) {
          poster = document.createElement('img');
          poster.className = 'event-slider__poster';
          poster.src = posterAttr;
          poster.alt = '';
          poster.loading = 'lazy';
          wrap.appendChild(poster);
        }
      }
      wrap.classList.remove('is-playing');
      wrap.classList.remove(REVEAL_CLASS);
    });
  }

  function revealPosterToVideo(wrap) {
    if (!wrap || !isSliderActive() || !wrap.classList.contains("is-playing")) return;
    requestAnimationFrame(() => {
      if (wrap.classList.contains("is-playing")) {
        wrap.classList.add(REVEAL_CLASS);
      }
    });
  }

  /** After first decoded / playing frame (or embed paint), fade poster away on desktop. */
  function bindDesktopRevealForLocalVideo(wrap) {
    if (!wrap || !isSliderActive()) return;
    const video = wrap.querySelector("video");
    if (!video) return;

    const reveal = () => {
      if (!wrap.classList.contains("is-playing")) return;
      revealPosterToVideo(wrap);
    };
    if (video.readyState >= 2 && !video.paused) {
      reveal();
      return;
    }
    video.addEventListener("playing", reveal, { once: true });
    video.addEventListener(
      "canplay",
      () => {
        if (!video.paused) reveal();
      },
      { once: true }
    );
    window.setTimeout(reveal, 2800);
  }

  function bindDesktopRevealForIframe(wrap, iframe) {
    if (!wrap || !isSliderActive()) return;
    const reveal = () => {
      if (!wrap.classList.contains("is-playing")) return;
      revealPosterToVideo(wrap);
    };
    if (iframe) {
      iframe.addEventListener("load", () => window.setTimeout(reveal, 380), { once: true });
    }
    window.setTimeout(reveal, 1600);
  }

  function playVideo(wrap) {
    if (!wrap) return;
    wrap.classList.add('is-playing');
    if (isSliderActive()) {
      wrap.classList.remove(REVEAL_CLASS);
    }
    const { type, src, id, posterSrc } = readVideoWrapData(wrap);

    if (isWrapLocalVideo(wrap)) {
      mountAndPlayLocalVideo(wrap, src, posterSrc);
      bindDesktopRevealForLocalVideo(wrap);
    } else if (type === 'youtube' && id) {
      if (wrap.dataset.youtubeEmbedLoading === '1') return;

      const existingHost = wrap.querySelector(`.${EMBED_CLASS}`);
      if (existingHost) {
        const ifr = findEmbedIframe(wrap);
        if (ifr) sendYoutubeCommand(ifr, 'play');
        markMobileEmbedActive(wrap);
        bindDesktopRevealForIframe(wrap, ifr);
        return;
      }

      wrap.dataset.youtubeEmbedLoading = '1';
      createYoutubeEmbed(wrap, id, EMBED_CLASS)
        .then(() => {
          delete wrap.dataset.youtubeEmbedLoading;
          markMobileEmbedActive(wrap);
          bindDesktopRevealForIframe(wrap, findEmbedIframe(wrap));
        })
        .catch(() => {
          delete wrap.dataset.youtubeEmbedLoading;
        });
    } else if (type === 'vimeo' && id) {
      const existingHost = wrap.querySelector(`.${EMBED_CLASS}`);
      if (existingHost) {
        const ifr = findEmbedIframe(wrap);
        if (ifr) sendVimeoCommand(ifr, 'play');
        markMobileEmbedActive(wrap);
        bindDesktopRevealForIframe(wrap, ifr);
        return;
      }
      appendVimeoEmbed(wrap, id, EMBED_CLASS);
      markMobileEmbedActive(wrap);
      bindDesktopRevealForIframe(wrap, findEmbedIframe(wrap));
    }
  }

  function stopVideo(wrap, fullTeardown = false) {
    if (!wrap) return;
    const mobileKeepEmbed = isMobilePortfolio() && !fullTeardown;

    if (mobileKeepEmbed) {
      wrap.classList.remove('is-playing');
      wrap.classList.remove(REVEAL_CLASS);
      pauseMobileEmbed(wrap);
      return;
    }

    wrap.classList.remove('is-playing');
    wrap.classList.remove(REVEAL_CLASS);
    wrap.classList.remove(MOBILE_EMBED_CLASS);
    delete wrap.dataset.youtubeEmbedLoading;
    if (isWrapLocalVideo(wrap)) {
      const video = wrap.querySelector("video");
      if (video) {
        video.pause();
        video.currentTime = 0;
      }
    } else {
      wrap.querySelectorAll(`.${EMBED_CLASS}`).forEach((el) => el.remove());
      wrap.querySelectorAll('iframe').forEach((el) => el.remove());
    }
  }

  function updateActiveSlide(slides, activeIndex, playVideoFn, stopVideoFn) {
    slides.forEach((slide, index) => {
      const videoWrap = slide.querySelector('.event-slider__video-wrap');
      if (index === activeIndex) {
        if (!slide.classList.contains('active')) {
          slide.classList.add('active');
          if (videoWrap) playVideoFn(videoWrap);
        }
      } else {
        if (slide.classList.contains('active')) {
          slide.classList.remove('active');
          if (videoWrap) stopVideoFn(videoWrap);
        }
      }
    });
  }

  function killViewportVideoTriggers(instance) {
    (instance.viewportVideoTriggers || []).forEach((st) => st.kill());
    instance.viewportVideoTriggers = [];
  }

  function setupViewportVideo(instance, slides) {
    killViewportVideoTriggers(instance);
    if (!slides.length) return;
    slides.forEach((slide) => {
      const videoWrap = slide.querySelector('.event-slider__video-wrap');
      if (!videoWrap) return;
      const mobile = isMobilePortfolio();
      const st = ScrollTrigger.create({
        trigger: videoWrap,
        start: mobile ? "top 98%" : "top 85%",
        end: mobile ? "bottom 2%" : "bottom 20%",
        onEnter: () => playVideo(videoWrap),
        onLeaveBack: () => stopVideo(videoWrap),
        onLeave: () => stopVideo(videoWrap),
        onEnterBack: () => playVideo(videoWrap),
      });
      instance.viewportVideoTriggers.push(st);
      if (st.isActive) playVideo(videoWrap);
    });
  }

  function pauseAutoplay(instance) {
    if (instance.autoplayId != null) {
      window.clearTimeout(instance.autoplayId);
      instance.autoplayId = null;
    }
  }

  function unbindSliderAutoplay(instance) {
    if (typeof instance.sliderAutoplayCleanup === "function") {
      instance.sliderAutoplayCleanup();
      instance.sliderAutoplayCleanup = null;
    }
    pauseAutoplay(instance);
    if (instance.slideTween) {
      instance.slideTween.kill();
      instance.slideTween = null;
    }
    instance.sliderHovered = false;
    instance.hoveredSlide = null;
    instance.sliderInView = false;
    instance.dragState = null;
    instance.dragPrevPrepared = false;
    instance.dragProgress = 0;
    instance.dragSuppressClick = false;
    instance.sliderContainer?.classList.remove("is-dragging");
  }

  const CLONE_ATTR = "data-portfolio-clone";

  function stripLoopClones(track) {
    if (!track) return;
    track.querySelectorAll(`[${CLONE_ATTR}]`).forEach((el) => el.remove());
  }

  function restoreSlideOrder(instance) {
    const { track, slides } = instance;
    if (!track || !slides?.length) return;
    stripLoopClones(track);
    Array.from(slides).forEach((slide) => track.appendChild(slide));
  }

  function getOriginalSlides(instance) {
    return Array.from(
      instance.track?.querySelectorAll(`.slide-item:not([${CLONE_ATTR}])`) || []
    );
  }

  function refreshLoopClones(instance) {
    const { track } = instance;
    const layout = instance.sliderLayout;
    if (!track || !layout) return;

    stripLoopClones(track);
    const originals = getOriginalSlides(instance);
    originals.forEach((slide) => {
      const clone = slide.cloneNode(true);
      clone.setAttribute(CLONE_ATTR, "");
      clone.setAttribute("data-clone-of", slide.getAttribute("data-portfolio-id") || "");
      clone.classList.remove("active");
      clone.querySelectorAll("video, iframe, .event-slider__video-embed").forEach((el) => {
        el.remove();
      });
      const wrap = clone.querySelector(".event-slider__video-wrap");
      if (wrap) {
        wrap.classList.remove("is-playing", REVEAL_CLASS, MOBILE_EMBED_CLASS);
      }
      gsap.set(clone, slideFlexTweenProps(layout.narrow, 10));
      track.appendChild(clone);
    });
  }

  function applySliderClip(instance, startX) {
    const { sliderContainer } = instance;
    if (!sliderContainer) return;
    sliderContainer.style.setProperty(
      "--portfolio-clip-left",
      `${Math.max(0, startX || 0)}px`
    );
  }

  function clearSliderClip(instance) {
    instance.sliderContainer?.style.removeProperty("--portfolio-clip-left");
  }

  function getTrackSlides(instance) {
    return getOriginalSlides(instance);
  }

  function uninitSlider(instance) {
    const { section, track, slides } = instance;
    unbindSliderAutoplay(instance);
    restoreSlideOrder(instance);
    clearSliderClip(instance);
    if (instance.sliderScrollTrigger) {
      instance.sliderScrollTrigger.kill();
      instance.sliderScrollTrigger = null;
    }
    if (instance.sliderTimeline) {
      instance.sliderTimeline.kill();
      instance.sliderTimeline = null;
    }
    section.classList.remove("portfolio-slider--width-via-gsap");
    gsap.set(Array.from(slides), {
      clearProps: "width,flexBasis,flexGrow,flexShrink,zIndex",
    });
    gsap.set(track, { clearProps: 'x' });
    slides.forEach((slide) => {
      slide.classList.remove('active');
      const videoWrap = slide.querySelector('.event-slider__video-wrap');
      if (videoWrap) stopVideo(videoWrap, true);
    });
    slides[0].classList.add('active');
    setupViewportVideo(instance, slides);
  }

  function teardownSliderOnly(instance) {
    const { section, track, slides } = instance;
    unbindSliderAutoplay(instance);
    restoreSlideOrder(instance);
    clearSliderClip(instance);
    if (instance.sliderScrollTrigger) {
      instance.sliderScrollTrigger.kill();
      instance.sliderScrollTrigger = null;
    }
    if (instance.sliderTimeline) {
      instance.sliderTimeline.kill();
      instance.sliderTimeline = null;
    }
    section.classList.remove("portfolio-slider--width-via-gsap");
    gsap.set(Array.from(slides), {
      clearProps: "width,flexBasis,flexGrow,flexShrink,zIndex",
    });
    gsap.set(track, { clearProps: 'x' });
    slides.forEach((slide) => {
      slide.classList.remove('active');
    });
  }

  /**
   * Track translateX so slide 0 starts at the .container content edge (after border + padding).
   * Uses layout measurements + computed padding; ~0 when .slider-container is already in normal flow
   * inside .container, non-zero if the theme offsets the slider row.
   */
  function getTrackStartOffsetX(containerEl, sliderContainer) {
    if (!containerEl || !sliderContainer) {
      return 0;
    }
    const containerRect = containerEl.getBoundingClientRect();
    const sliderRect = sliderContainer.getBoundingClientRect();
    const cs = getComputedStyle(containerEl);
    const paddingLeft = parseFloat(cs.paddingLeft) || 0;
    const borderLeft = parseFloat(cs.borderLeftWidth) || 0;
    const containerContentLeft = containerRect.left + borderLeft + paddingLeft;
    return containerContentLeft - sliderRect.left;
  }

  /**
   * Desktop slider (>=1024): start X from container padding alignment; slide step from gap + slide width.
   */
  function getDesktopSliderLayoutMetrics(containerEl, sliderContainer, track, slides) {
    const fallbackGap = 24;
    const fallbackSlide = 390;

    const trackCs = track ? getComputedStyle(track) : null;
    const gap = trackCs ? parseFloat(trackCs.gap || trackCs.columnGap || "0") || 0 : fallbackGap;

    let inactiveSlideWidth = fallbackSlide;
    if (slides?.length > 1) {
      inactiveSlideWidth = slides[1].getBoundingClientRect().width || fallbackSlide;
    } else if (slides?.length === 1) {
      inactiveSlideWidth = slides[0].getBoundingClientRect().width || fallbackSlide;
    }

    const slideStep = inactiveSlideWidth + gap;

    if (!isSliderActive() || !containerEl) {
      return {
        startX: 0,
        slideStep,
        padLeft: 0,
        padRight: 0,
        borderLeft: 0,
        borderRight: 0,
      };
    }

    const cs = getComputedStyle(containerEl);
    const padLeft = parseFloat(cs.paddingLeft) || 0;
    const padRight = parseFloat(cs.paddingRight) || 0;
    const borderLeft = parseFloat(cs.borderLeftWidth) || 0;
    const borderRight = parseFloat(cs.borderRightWidth) || 0;
    const startX = getTrackStartOffsetX(containerEl, sliderContainer);

    return {
      startX,
      slideStep,
      padLeft,
      padRight,
      borderLeft,
      borderRight,
    };
  }

  /** Measured inactive vs active slide widths (matches CSS at current breakpoint). */
  function measurePortfolioSlideWidths(slideList) {
    const list = Array.from(slideList);
    if (!list.length) {
      return { narrow: 390, wide: 808 };
    }
    list.forEach((s) => s.classList.remove("active"));
    const narrow = list[0].getBoundingClientRect().width || 390;
    list[0].classList.add("active");
    const wide = list[0].getBoundingClientRect().width || 808;
    list[0].classList.remove("active");
    list[0].classList.add("active");
    return { narrow, wide };
  }

  /** Which slide is visually expanded (syncs .active / video with scrubbed widths). */
  function getActiveSlideIndexFromLayout(slideList) {
    let best = 0;
    let maxW = -1;
    Array.from(slideList).forEach((slide, index) => {
      const w = slide.getBoundingClientRect().width;
      if (w > maxW) {
        maxW = w;
        best = index;
      }
    });
    return best;
  }

  const slideFlexTweenProps = (px, z) => ({
    width: px,
    flexBasis: px,
    flexGrow: 0,
    flexShrink: 0,
    zIndex: z,
  });

  const AUTO_SLIDE_MS = 2000;
  const SLIDE_TWEEN_DURATION = 0.8;
  const DRAG_THRESHOLD_PX = 8;
  const DRAG_SNAP_RATIO = 0.22;
  const DRAG_VELOCITY = 0.35;

  function isSlideTweenPlaying(instance) {
    const tw = instance.slideTween;
    return !!(tw && tw.progress() < 1);
  }

  function getActiveSlideEl(instance) {
    const slides = getTrackSlides(instance);
    return slides.find((slide) => slide.classList.contains("active")) || slides[0] || null;
  }

  function isHoveringActiveSlide(instance) {
    const active = getActiveSlideEl(instance);
    return !!(active && instance.hoveredSlide === active);
  }

  function scheduleAutoplay(instance) {
    pauseAutoplay(instance);
    if (!isSliderActive() || instance.totalSlides < 2) return;
    if (isHoveringActiveSlide(instance) || !instance.sliderInView) return;
    instance.autoplayId = window.setTimeout(() => {
      instance.autoplayId = null;
      goToSlide(instance, 1);
    }, AUTO_SLIDE_MS);
  }

  function syncActiveHoverPause(instance) {
    const hoveringActive = isHoveringActiveSlide(instance);
    if (hoveringActive) {
      instance.sliderHovered = true;
      pauseAutoplay(instance);
      if (isSlideTweenPlaying(instance)) {
        instance.slideTween.pause();
      }
      return;
    }
    if (instance.sliderHovered) {
      instance.sliderHovered = false;
      resumeSliderMotion(instance);
    }
  }

  function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }

  function setSlideWidthsForProgress(instance, fromIndex, localT) {
    const { narrow, wide } = instance.sliderLayout || {};
    const slides = getOriginalSlides(instance);
    slides.forEach((slide, j) => {
      let width = narrow;
      if (j === fromIndex) width = wide - (wide - narrow) * localT;
      else if (j === fromIndex + 1) width = narrow + (wide - narrow) * localT;
      gsap.set(
        slide,
        slideFlexTweenProps(width, j === fromIndex || j === fromIndex + 1 ? 15 : 10)
      );
    });
    updateActiveSlide(
      slides,
      getActiveSlideIndexFromLayout(slides),
      playVideo,
      stopVideo
    );
  }

  function preparePrevSlide(instance) {
    if (instance.dragPrevPrepared) return;
    const { track } = instance;
    const layout = instance.sliderLayout;
    if (!track || !layout) return;

    const originals = getOriginalSlides(instance);
    if (originals.length < 2) return;

    const { startX, slideStep, narrow, wide } = layout;
    track.insertBefore(originals[originals.length - 1], originals[0]);
    refreshLoopClones(instance);

    const slides = getOriginalSlides(instance);
    gsap.set(track, { x: startX - slideStep });
    slides.forEach((slide, j) => {
      gsap.set(slide, slideFlexTweenProps(j === 1 ? wide : narrow, j === 1 ? 15 : 10));
    });
    layout.slideArr = slides;
    instance.dragPrevPrepared = true;
    updateActiveSlide(slides, 1, playVideo, stopVideo);
  }

  function unpreparePrevSlide(instance) {
    if (!instance.dragPrevPrepared) return;
    const { track } = instance;
    const layout = instance.sliderLayout;
    if (!track || !layout) return;

    const { startX, narrow, wide } = layout;
    const originals = getOriginalSlides(instance);
    const prev = originals[0];
    const firstClone = track.querySelector(`[${CLONE_ATTR}]`);
    if (firstClone) track.insertBefore(prev, firstClone);
    else track.appendChild(prev);
    refreshLoopClones(instance);

    const slides = getOriginalSlides(instance);
    gsap.set(track, { x: startX });
    slides.forEach((slide, j) => {
      gsap.set(slide, slideFlexTweenProps(j === 0 ? wide : narrow, j === 0 ? 15 : 10));
    });
    layout.slideArr = slides;
    instance.dragPrevPrepared = false;
    instance.activeIndex = 0;
    updateActiveSlide(slides, 0, playVideo, stopVideo);
  }

  function applyDragOffset(instance, dx) {
    const { track } = instance;
    const layout = instance.sliderLayout;
    if (!track || !layout) return;

    const { startX, slideStep } = layout;
    const originals = getOriginalSlides(instance);
    if (originals.length < 2) return;

    if (dx > 0 && !instance.dragPrevPrepared) preparePrevSlide(instance);
    if (dx <= 0 && instance.dragPrevPrepared) unpreparePrevSlide(instance);

    if (instance.dragPrevPrepared) {
      const t = clamp(dx / slideStep, 0, 1);
      gsap.set(track, { x: startX - slideStep + slideStep * t });
      setSlideWidthsForProgress(instance, 0, 1 - t);
      instance.dragProgress = -t;
      return;
    }

    const t = clamp(-dx / slideStep, 0, originals.length - 1);
    const from = Math.floor(t);
    const local = t - from;
    gsap.set(track, { x: startX - slideStep * t });
    setSlideWidthsForProgress(instance, from, local);
    instance.dragProgress = t;
  }

  function settleMotionForDrag(instance) {
    if (!isSlideTweenPlaying(instance)) return;

    const { track } = instance;
    const layout = instance.sliderLayout;
    const slides = getTrackSlides(instance);
    const nearest = getActiveSlideIndexFromLayout(slides);
    instance.slideTween.kill();
    instance.slideTween = null;
    if (!layout || !track || !slides.length) return;

    const { startX, narrow, wide } = layout;

    if (instance.dragPrevPrepared) {
      if (nearest === 0) {
        instance.dragPrevPrepared = false;
        instance.activeIndex = 0;
        gsap.set(track, { x: startX });
        slides.forEach((slide, j) => {
          gsap.set(slide, slideFlexTweenProps(j === 0 ? wide : narrow, j === 0 ? 15 : 10));
        });
        updateActiveSlide(slides, 0, playVideo, stopVideo);
      } else {
        unpreparePrevSlide(instance);
      }
      return;
    }

    if (nearest > 0) {
      rotatePassedSlides(instance, nearest);
      return;
    }

    gsap.set(track, { x: startX });
    slides.forEach((slide, j) => {
      gsap.set(slide, slideFlexTweenProps(j === 0 ? wide : narrow, j === 0 ? 15 : 10));
    });
    instance.activeIndex = 0;
    updateActiveSlide(slides, 0, playVideo, stopVideo);
  }

  function finishDrag(instance, velocityX) {
    const layout = instance.sliderLayout;
    if (!layout) return;

    const originals = getOriginalSlides(instance);
    const t = instance.dragProgress || 0;

    if (instance.dragPrevPrepared) {
      const commitPrev = -t >= DRAG_SNAP_RATIO || velocityX > DRAG_VELOCITY;
      goToSlide(instance, commitPrev ? 0 : 1, { fromDrag: true });
      return;
    }

    let target = Math.round(t);
    if (velocityX < -DRAG_VELOCITY) target = Math.min(originals.length - 1, Math.ceil(t));
    if (velocityX > DRAG_VELOCITY) target = Math.max(0, Math.floor(t));
    target = clamp(target, 0, Math.max(0, originals.length - 1));
    goToSlide(instance, target, { fromDrag: true });
  }

  function rotatePassedSlides(instance, count) {
    const { track } = instance;
    const layout = instance.sliderLayout;
    if (!track || !layout || count <= 0) return;

    const { startX, narrow, wide } = layout;
    const firstClone = track.querySelector(`[${CLONE_ATTR}]`);
    getOriginalSlides(instance)
      .slice(0, count)
      .forEach((slide) => {
        if (firstClone) track.insertBefore(slide, firstClone);
        else track.appendChild(slide);
      });
    refreshLoopClones(instance);

    gsap.set(track, { x: startX });
    const slides = getTrackSlides(instance);
    slides.forEach((slide, j) => {
      gsap.set(slide, slideFlexTweenProps(j === 0 ? wide : narrow, j === 0 ? 15 : 10));
    });
    instance.activeIndex = 0;
    layout.slideArr = slides;
    updateActiveSlide(slides, 0, playVideo, stopVideo);
  }

  function goToSlide(instance, index, options = {}) {
    const { fromDrag = false } = options;
    const { track } = instance;
    const layout = instance.sliderLayout;
    if (!layout || !track) return;

    const slides = getTrackSlides(instance);
    if (index < 0 || index >= slides.length) return;
    if (!fromDrag && index === 0) return;
    if (!fromDrag && index === instance.activeIndex) return;
    if (
      fromDrag &&
      !instance.dragPrevPrepared &&
      index === 0 &&
      Math.abs(instance.dragProgress || 0) < 0.001
    ) {
      instance.hoveredSlide =
        getTrackSlides(instance).find((slide) => slide.matches(":hover")) || null;
      syncActiveHoverPause(instance);
      if (!instance.sliderHovered && instance.sliderInView) {
        scheduleAutoplay(instance);
      }
      return;
    }

    const { startX, slideStep, narrow, wide, ease } = layout;

    pauseAutoplay(instance);
    if (instance.slideTween) {
      instance.slideTween.kill();
      instance.slideTween = null;
    }

    instance.slideTween = gsap.timeline({
      onUpdate: () => {
        updateActiveSlide(
          slides,
          getActiveSlideIndexFromLayout(slides),
          playVideo,
          stopVideo
        );
      },
      onComplete: () => {
        if (instance.dragPrevPrepared) {
          if (index === 0) {
            instance.dragPrevPrepared = false;
            instance.activeIndex = 0;
            layout.slideArr = slides;
            updateActiveSlide(slides, 0, playVideo, stopVideo);
          } else {
            unpreparePrevSlide(instance);
          }
        } else if (index > 0) {
          rotatePassedSlides(instance, index);
        } else {
          instance.activeIndex = 0;
          updateActiveSlide(slides, 0, playVideo, stopVideo);
        }
        instance.dragProgress = 0;
        instance.hoveredSlide =
          getTrackSlides(instance).find((slide) => slide.matches(":hover")) || null;
        syncActiveHoverPause(instance);
        if (!instance.sliderHovered && instance.sliderInView) {
          scheduleAutoplay(instance);
        }
      },
    });

    instance.slideTween.to(
      track,
      { x: startX - slideStep * index, duration: SLIDE_TWEEN_DURATION, ease },
      0
    );
    slides.forEach((slide, j) => {
      instance.slideTween.to(
        slide,
        {
          ...slideFlexTweenProps(j === index ? wide : narrow, j === index ? 15 : 10),
          duration: SLIDE_TWEEN_DURATION,
          ease,
        },
        0
      );
    });
  }

  function resumeSliderMotion(instance) {
    if (isSlideTweenPlaying(instance)) {
      instance.slideTween.resume();
      return;
    }
    scheduleAutoplay(instance);
  }

  function bindSliderAutoplay(instance) {
    const { section, slides } = instance;
    if (!section) return;

    unbindSliderAutoplay(instance);

    const slideArr = Array.from(slides);

    const onSlideEnter = (event) => {
      instance.hoveredSlide = event.currentTarget;
      syncActiveHoverPause(instance);
    };

    const onSlideLeave = (event) => {
      if (instance.hoveredSlide === event.currentTarget) {
        instance.hoveredSlide = null;
      }
      syncActiveHoverPause(instance);
    };

    const resolveOriginalSlide = (slide) => {
      if (!slide) return null;
      if (!slide.hasAttribute(CLONE_ATTR)) return slide;
      const id = slide.getAttribute("data-clone-of");
      return (
        getOriginalSlides(instance).find(
          (item) => item.getAttribute("data-portfolio-id") === id
        ) || null
      );
    };

    const onSlideClick = (event) => {
      if (instance.dragSuppressClick) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      const slide = event.target.closest(".slide-item");
      const original = resolveOriginalSlide(slide);
      const index = getOriginalSlides(instance).indexOf(original);
      if (index <= 0) return;
      goToSlide(instance, index);
    };

    const dragSurface = instance.sliderContainer || instance.track;

    const endPointerDrag = (event) => {
      const drag = instance.dragState;
      if (!drag || drag.pointerId !== event.pointerId) return;

      if (drag.captured && dragSurface.hasPointerCapture?.(drag.pointerId)) {
        dragSurface.releasePointerCapture(drag.pointerId);
      }
      dragSurface.classList.remove("is-dragging");

      if (drag.didDrag) {
        instance.dragSuppressClick = true;
        window.setTimeout(() => {
          instance.dragSuppressClick = false;
        }, 0);
        finishDrag(instance, drag.velocityX || 0);
      } else if (!isHoveringActiveSlide(instance) && instance.sliderInView) {
        scheduleAutoplay(instance);
      }

      instance.dragState = null;
    };

    const onPointerDown = (event) => {
      if (!isSliderActive() || instance.totalSlides < 2) return;
      if (event.pointerType === "mouse" && event.button !== 0) return;
      settleMotionForDrag(instance);

      pauseAutoplay(instance);
      instance.dragSuppressClick = false;
      instance.dragState = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        lastX: event.clientX,
        lastTime: performance.now(),
        velocityX: 0,
        didDrag: false,
        captured: false,
      };
    };

    const onPointerMove = (event) => {
      const drag = instance.dragState;
      if (!drag || drag.pointerId !== event.pointerId) return;

      const dx = event.clientX - drag.startX;
      const dy = event.clientY - drag.startY;
      const now = performance.now();
      const dt = now - drag.lastTime;
      if (dt > 0) drag.velocityX = (event.clientX - drag.lastX) / dt;
      drag.lastX = event.clientX;
      drag.lastTime = now;

      if (!drag.didDrag) {
        if (Math.abs(dx) < DRAG_THRESHOLD_PX && Math.abs(dy) < DRAG_THRESHOLD_PX) return;
        if (Math.abs(dy) > Math.abs(dx)) {
          instance.dragState = null;
          if (!isHoveringActiveSlide(instance) && instance.sliderInView) {
            scheduleAutoplay(instance);
          }
          return;
        }
        drag.didDrag = true;
        drag.captured = true;
        dragSurface.classList.add("is-dragging");
        dragSurface.setPointerCapture?.(drag.pointerId);
      }

      event.preventDefault();
      applyDragOffset(instance, dx);
    };

    const onClickCapture = (event) => {
      if (!instance.dragSuppressClick) return;
      event.preventDefault();
      event.stopPropagation();
      instance.dragSuppressClick = false;
    };

    slideArr.forEach((slide) => {
      slide.addEventListener("pointerenter", onSlideEnter);
      slide.addEventListener("pointerleave", onSlideLeave);
    });
    instance.track?.addEventListener("click", onSlideClick);
    dragSurface?.addEventListener("pointerdown", onPointerDown);
    dragSurface?.addEventListener("pointermove", onPointerMove);
    dragSurface?.addEventListener("pointerup", endPointerDrag);
    dragSurface?.addEventListener("pointercancel", endPointerDrag);
    dragSurface?.addEventListener("click", onClickCapture, true);

    instance.sliderScrollTrigger = ScrollTrigger.create({
      trigger: section,
      start: "top 80%",
      end: "bottom 20%",
      onToggle: (self) => {
        instance.sliderInView = self.isActive;
        if (self.isActive) {
          if (!isHoveringActiveSlide(instance)) resumeSliderMotion(instance);
        } else {
          pauseAutoplay(instance);
          if (isSlideTweenPlaying(instance)) {
            instance.slideTween.pause();
          }
        }
      },
    });

    instance.sliderInView = !!instance.sliderScrollTrigger.isActive;
    instance.hoveredSlide = slideArr.find((slide) => slide.matches(":hover")) || null;
    instance.sliderHovered = isHoveringActiveSlide(instance);
    if (instance.sliderHovered) {
      pauseAutoplay(instance);
    } else if (instance.sliderInView) {
      scheduleAutoplay(instance);
    }

    instance.sliderAutoplayCleanup = () => {
      pauseAutoplay(instance);
      slideArr.forEach((slide) => {
        slide.removeEventListener("pointerenter", onSlideEnter);
        slide.removeEventListener("pointerleave", onSlideLeave);
      });
      instance.track?.removeEventListener("click", onSlideClick);
      dragSurface?.classList.remove("is-dragging");
      dragSurface?.removeEventListener("pointerdown", onPointerDown);
      dragSurface?.removeEventListener("pointermove", onPointerMove);
      dragSurface?.removeEventListener("pointerup", endPointerDrag);
      dragSurface?.removeEventListener("pointercancel", endPointerDrag);
      dragSurface?.removeEventListener("click", onClickCapture, true);
      instance.dragState = null;
      instance.dragSuppressClick = false;
      if (instance.sliderScrollTrigger) {
        instance.sliderScrollTrigger.kill();
        instance.sliderScrollTrigger = null;
      }
      instance.sliderHovered = false;
      instance.hoveredSlide = null;
      instance.sliderInView = false;
    };
  }

  function initSliderOnly(instance) {
    const { section, containerEl, sliderContainer, track, slides } = instance;

    unbindSliderAutoplay(instance);
    if (instance.sliderTimeline) {
      instance.sliderTimeline.kill();
      instance.sliderTimeline = null;
    }
    instance.sliderScrollTrigger = null;

    // Apply before any layout reads: CSS otherwise keeps width/flex transitions (0.4s) on
    // .slide-item; crossing ~1540px changes breakpoints and mid-transition measurements break
    // track shift, endX, and ScrollTrigger pin/scrub math.
    section.classList.add("portfolio-slider--width-via-gsap");
    void section.offsetHeight;
    restoreSlideOrder(instance);
    instance.dragPrevPrepared = false;
    instance.dragProgress = 0;
    instance.dragState = null;

    const { startX } = getDesktopSliderLayoutMetrics(
      containerEl,
      sliderContainer,
      track,
      slides
    );
    const EASE = 'power2.inOut';
    const slideArr = Array.from(slides);
    slideArr.forEach((slide, i) => {
      slide.setAttribute("data-portfolio-id", String(i));
    });
    const { narrow, wide } = measurePortfolioSlideWidths(slideArr);
    const trackCs = getComputedStyle(track);
    const gap = parseFloat(trackCs.gap || trackCs.columnGap || "0") || 0;
    const slideStep = narrow + gap;

    applySliderClip(instance, startX);
    gsap.set(track, { x: startX });
    slideArr.forEach((slide, j) => {
      gsap.set(
        slide,
        slideFlexTweenProps(j === 0 ? wide : narrow, j === 0 ? 15 : 10)
      );
    });

    instance.activeIndex = 0;
    instance.sliderLayout = {
      startX,
      slideStep,
      narrow,
      wide,
      slideArr,
      ease: EASE,
    };
    refreshLoopClones(instance);

    bindSliderAutoplay(instance);
  }

  function initInstance(section) {
    const containerEl = section.querySelector('.container');
    const sliderContainer = section.querySelector('.slider-container');
    const track = section.querySelector('.slider-track');
    const header = section.querySelector('.slider-header');
    const slides = section.querySelectorAll(`.slide-item:not([${CLONE_ATTR}])`);
    const totalSlides = slides.length;

    if (!containerEl || !sliderContainer || !track || !header || slides.length === 0) return null;

    const instance = {
      section,
      containerEl,
      sliderContainer,
      track,
      header,
      slides,
      totalSlides,
      sliderScrollTrigger: null,
      sliderTimeline: null,
      sliderLayout: null,
      slideTween: null,
      sliderAutoplayCleanup: null,
      autoplayId: null,
      activeIndex: 0,
      sliderHovered: false,
      hoveredSlide: null,
      sliderInView: false,
      dragState: null,
      dragPrevPrepared: false,
      dragProgress: 0,
      dragSuppressClick: false,
      viewportVideoTriggers: [],
    };

    setupPosters(slides);
    slides.forEach(s => s.classList.remove('active'));
    slides[0].classList.add('active');
    const firstWrap = slides[0].querySelector('.event-slider__video-wrap');

    if (isSliderActive()) {
      if (firstWrap) playVideo(firstWrap);
      killViewportVideoTriggers(instance);
      gsap.from(header, {
        y: 50,
        opacity: 0,
        duration: 1,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: section,
          start: 'top 80%',
        }
      });
      initSliderOnly(instance);
    } else {
      gsap.set(track, { clearProps: 'x' });
      setupViewportVideo(instance, slides);
    }

    return instance;
  }

  function run() {
    const sections = document.querySelectorAll('.portfolio-slider');
    if (!sections.length) return;

    const instances = [];
    sections.forEach((section) => {
      const instance = initInstance(section);
      if (instance) instances.push(instance);
    });

    if (!instances.length) return;

    const handleResize = () => {
      instances.forEach((instance) => {
        const { section, track, slides } = instance;
        if (!section || !track || !slides.length) return;

        if (isSliderActive()) {
          killViewportVideoTriggers(instance);
          if (instance.sliderScrollTrigger || instance.sliderTimeline) {
            teardownSliderOnly(instance);
          }
          slides.forEach((slide) => {
            const wrap = slide.querySelector('.event-slider__video-wrap');
            if (wrap) stopVideo(wrap, true);
          });
          slides[0].classList.add('active');
          const firstWrapEl = slides[0].querySelector('.event-slider__video-wrap');
          if (firstWrapEl) playVideo(firstWrapEl);
          initSliderOnly(instance);
        } else {
          if (instance.sliderScrollTrigger || instance.sliderTimeline || instance.sliderLayout) {
            uninitSlider(instance);
          }
        }
      });
    };

    let resizeTimeoutId = null;
    let resizeRefreshRafId = null;
    const RESIZE_DEBOUNCE_MS = 180;
    window.addEventListener('resize', () => {
      if (resizeRefreshRafId !== null) {
        cancelAnimationFrame(resizeRefreshRafId);
      }
      resizeRefreshRafId = requestAnimationFrame(() => {
        resizeRefreshRafId = null;
        ScrollTrigger.refresh();
      });

      if (resizeTimeoutId !== null) {
        window.clearTimeout(resizeTimeoutId);
      }
      resizeTimeoutId = window.setTimeout(() => {
        resizeTimeoutId = null;
        handleResize();
        ScrollTrigger.refresh();
      }, RESIZE_DEBOUNCE_MS);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
}